export type AuthErrorSeverity = "low" | "medium" | "high" | "critical";

type AuthAction =
  | "login"
  | "refresh"
  | "retry"
  | "contact_support"
  | "none"
  | "verify_email"
  | "wait"
  | "logout";

export interface AuthErrorMetadata {
  [key: string]: unknown;
}

export interface AuthErrorPayload {
  code: string;
  message: string;
  user_message?: string;
  severity: AuthErrorSeverity;
  action: AuthAction;
  metadata?: AuthErrorMetadata;
  timestamp?: string;
  request_id?: string;
  correlation_id?: string;
  should_log_out: boolean;
  can_retry: boolean;
  is_temporary?: boolean;
  http_status_code?: number;
}

export interface AuthErrorResponse {
  status: "error";
  error: AuthErrorPayload;
}

export interface StringAuthErrorResponse {
  status: "error";
  error: string;
  message?: string;
}

const LOGOUT_ERROR_CODES = new Set([
  "AUTH_SESSION_BLACKLISTED",
  "AUTH_SESSION_EXPIRED",
  "AUTH_SESSION_INVALID",
  "AUTH_SESSION_NOT_FOUND",
  "AUTH_SESSION_REVOKED_BY_USER",
  "session_revoked_by_user",
]);

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return undefined;
};

export const isAuthErrorResponse = (value: unknown): value is AuthErrorResponse => {
  const response = asRecord(value);
  if (!response) return false;
  if (response.status !== "error" || typeof response.error !== "object" || !response.error) {
    return false;
  }

  const error = response.error as Record<string, unknown>;
  return typeof error.code === "string";
};

export const getAuthErrorCode = (value: unknown): string | undefined => {
  const response = asRecord(value);
  if (!response) return undefined;

  if (typeof response.code === "string") {
    return response.code;
  }

  if (typeof response.error === "string") {
    return response.error;
  }

  const error = asRecord(response.error);
  if (typeof error?.code === "string") {
    return error.code;
  }

  return undefined;
};

export const getAuthUserMessage = (value: unknown): string | undefined => {
  const response = asRecord(value);
  if (!response) return undefined;

  const error = asRecord(response.error);
  if (typeof error?.user_message === "string" && error.user_message) {
    return error.user_message;
  }

  if (typeof response.message === "string" && response.message) {
    return response.message;
  }

  if (typeof error?.message === "string" && error.message) {
    return error.message;
  }

  if (typeof response.error === "string" && response.error) {
    return response.error;
  }

  return undefined;
};

export const shouldLogOutForAuthError = (value: unknown): boolean => {
  const response = asRecord(value);
  const error = asRecord(response?.error);

  if (error?.should_log_out === true) {
    return true;
  }

  const code = getAuthErrorCode(value);
  return code ? LOGOUT_ERROR_CODES.has(code) : false;
};
